import { useTranslation } from "react-i18next";

export default function ArticleContent() {
  const { t } = useTranslation("blog-kozijn-schilderen");

  const schilderenItems = t("sections.schilderen.costs.items", { returnObjects: true }) as Array<{
    label: string;
    price: string;
  }>;

  const vervangenItems = t("sections.vervangen.costs.items", { returnObjects: true }) as Array<{
    label: string;
    price: string;
  }>;

  const kostenRows = t("sections.kostenVergelijking.tableRows", { returnObjects: true }) as string[][];
  const kostenHead = t("sections.kostenVergelijking.tableHead", { returnObjects: true }) as string[];

  const links = t("sections.onsAdvies.links", { returnObjects: true }) as Array<{
    text: string;
    href: string;
  }>;

  return (
    <div className="flex flex-col gap-10 md:gap-14">
      {/* Schilderen */}
      <section id="kozijn-schilderen">
        <h2 className="font-geist font-bold text-xl md:text-2xl text-brand-text mb-3 md:mb-4">
          {t("sections.schilderen.h2")}
        </h2>
        <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-3">
          {t("sections.schilderen.p1")}
        </p>
        <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-5 md:mb-6">
          {t("sections.schilderen.p2")}
        </p>
        <div className="bg-brand-cream rounded-xl p-5 md:p-6 border border-brand-border mb-5 md:mb-6">
          <h3 className="font-geist font-semibold text-base md:text-lg text-brand-text mb-3 md:mb-4">
            {t("sections.schilderen.costs.intro")}
          </h3>
          <div className="flex flex-col gap-2.5 md:gap-3">
            {schilderenItems.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between py-2 border-b border-brand-border/40 last:border-0">
                <span className="font-geist text-sm text-brand-text/70">{item.label}</span>
                <span className="font-geist-mono text-sm font-semibold text-brand-text">{item.price}</span>
              </div>
            ))}
          </div>
          <p className="font-geist text-xs md:text-sm text-brand-text/50 italic mt-3 md:mt-4">
            {t("sections.schilderen.costs.note")}
          </p>
        </div>
      </section>

      {/* Vervangen */}
      <section id="kozijn-vervangen">
        <h2 className="font-geist font-bold text-xl md:text-2xl text-brand-text mb-3 md:mb-4">
          {t("sections.vervangen.h2")}
        </h2>
        <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-3">
          {t("sections.vervangen.p1")}
        </p>
        <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-5 md:mb-6">
          {t("sections.vervangen.p2")}
        </p>
        <div className="bg-brand-cream rounded-xl p-5 md:p-6 border border-brand-border mb-5 md:mb-6">
          <h3 className="font-geist font-semibold text-base md:text-lg text-brand-text mb-3 md:mb-4">
            {t("sections.vervangen.costs.intro")}
          </h3>
          <div className="flex flex-col gap-2.5 md:gap-3">
            {vervangenItems.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between py-2 border-b border-brand-border/40 last:border-0">
                <span className="font-geist text-sm text-brand-text/70">{item.label}</span>
                <span className="font-geist-mono text-sm font-semibold text-brand-text">{item.price}</span>
              </div>
            ))}
          </div>
          <p className="font-geist text-xs md:text-sm text-brand-text/50 italic mt-3 md:mt-4">
            {t("sections.vervangen.costs.note")}
          </p>
        </div>
      </section>

      {/* Kostenvergelijking */}
      <section id="kosten-vergelijken">
        <h2 className="font-geist font-bold text-xl md:text-2xl text-brand-text mb-3 md:mb-4">
          {t("sections.kostenVergelijking.h2")}
        </h2>
        <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-3">
          {t("sections.kostenVergelijking.p1")}
        </p>
        <div className="overflow-x-auto -mx-4 md:mx-0 px-4 md:px-0 mb-5 md:mb-6">
          <table className="w-full text-left border-collapse min-w-[560px]">
            <thead>
              <tr className="border-b-2 border-brand-border">
                {kostenHead.map((head, i) => (
                  <th key={i} className="font-geist font-semibold text-sm text-brand-text py-3 pr-4">
                    {head}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {kostenRows.map((row, ri) => (
                <tr key={ri} className="border-b border-brand-border/40">
                  {row.map((cell, ci) => (
                    <td key={ci} className={`font-geist text-sm py-3 pr-4 ${
                      ci === 0 ? "text-brand-text font-medium" : "text-brand-text/70"
                    }`}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bg-[#FFF9E0] border border-[#FFD600] rounded-xl p-4 md:p-5 flex gap-3 items-start">
          <i className="ri-lightbulb-line w-5 h-5 flex items-center justify-center text-brand-text mt-0.5 text-lg"></i>
          <p className="font-geist text-sm text-brand-text/80 leading-relaxed">
            {t("sections.kostenVergelijking.conclusion")}
          </p>
        </div>
      </section>

      {/* Energiebesparing + ISDE */}
      <section id="energiebesparing-isde">
        <h2 className="font-geist font-bold text-xl md:text-2xl text-brand-text mb-3 md:mb-4">
          {t("sections.energiebesparing.h2")}
        </h2>
        <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-3">
          {t("sections.energiebesparing.p1")}
        </p>
        <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-3">
          {t("sections.energiebesparing.p2")}
        </p>
        <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-5 md:mb-6">
          {t("sections.energiebesparing.p3")}
        </p>
        <a
          href={t("sections.energiebesparing.linkIsde.href")}
          className="inline-flex items-center gap-1.5 font-geist font-medium text-sm bg-brand-yellow text-brand-text px-5 py-2.5 rounded-full hover:bg-brand-yellow/90 transition-colors whitespace-nowrap"
        >
          {t("sections.energiebesparing.linkIsde.text")}
          <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center text-sm"></i>
        </a>
      </section>

      {/* Ons advies */}
      <section id="ons-advies" className="bg-brand-cream rounded-2xl p-6 md:p-10 border border-brand-border">
        <h2 className="font-geist font-bold text-xl md:text-2xl text-brand-text mb-3 md:mb-4">
          {t("sections.onsAdvies.h2")}
        </h2>
        <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-3">
          {t("sections.onsAdvies.p1")}
        </p>
        <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-5 md:mb-6">
          {t("sections.onsAdvies.p2")}
        </p>
        <div className="flex flex-wrap gap-3">
          {links.map((link, idx) => (
            <a
              key={idx}
              href={link.href}
              className={`inline-flex items-center gap-1.5 font-geist font-medium text-sm px-5 py-2.5 rounded-full transition-colors whitespace-nowrap ${
                idx === 0
                  ? "bg-brand-yellow text-brand-text hover:bg-brand-yellow/90"
                  : "border border-brand-border text-brand-text hover:bg-brand-cream"
              }`}
            >
              {link.text}
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center text-sm"></i>
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}